import { Injectable, computed, inject, signal } from '@angular/core';
import { UserState } from '@coin/shared/auth/feature';
import { CommunicationTypeEnum, TodoTaskType } from '@coin/shared/core/util-enums';
import { DateOperations } from '@coin/shared/core/util-helpers';
import { Topic } from '@coin/shared/core/util-models';
import { produce } from 'immer';
import { Observable, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CosmosDashboardService } from '@coin/shared/core/data-access';
import { Category } from '../models/category.model';
import { TopicsFilter } from '../models/topics-filter.model';

export interface TopicsStateModel {
  topics: Topic[];
  bannerTopics: Topic[];
  topicsFilter: TopicsFilter;
  loaded: boolean;
  loading: boolean;
}

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class TopicsState {
  private readonly dashboardService = inject(CosmosDashboardService);
  private readonly userState = inject(UserState);

  private readonly topicsStateModel = signal<TopicsStateModel>({
    topics: undefined,
    bannerTopics: undefined,
    topicsFilter: {
      categories: [],
      allItemsFilter: true
    },
    loaded: false,
    loading: false
  });

  public topics = computed(() => this.topicsStateModel().topics);

  // topics/bannerTopics are undefined until loadTopics() resolves; NGXS's `selectorOptions.suppressErrors: true`
  // (see app.module.ts) silently turned that TypeError into `undefined` for these selectors - replicated here explicitly.
  public filteredTopics = computed(() => {
    const state = this.topicsStateModel();
    return state.topics
      ?.filter(topic => state.topicsFilter.categories.find(category => category.type === topic.type).active)
      ?.sort((a, b) =>
        b.dateStart && a.dateStart ? new Date(b.dateStart).valueOf() - new Date(a.dateStart).valueOf() : 0
      );
  });

  public currentTopics = computed(() =>
    this.filteredTopics()?.filter(topic => {
      const dueDateIsReached = DateOperations.dueDateOverBasedOnDay(topic.dateEnd);
      return !dueDateIsReached && !topic.done;
    })
  );

  public visibleBannerTopics = computed(() =>
    this.topicsStateModel().bannerTopics?.filter(
      topic =>
        topic.type === CommunicationTypeEnum.Banner &&
        topic.showBanner &&
        !DateOperations.dueDateOverBasedOnDay(topic.dateEnd)
    )
  );

  public topicsFilter = computed(() => this.topicsStateModel().topicsFilter);

  public loading = computed(() => this.topicsStateModel().loading);

  loadTopics(): Observable<void> {
    this.topicsStateModel.update(state => ({ ...state, loading: true }));

    return this.dashboardService.getData().pipe(
      switchMap(topics => {
        this.loadTopicsSuccess(topics);
        this.userState.setDynamicMercerAccess(this.hasFinalIncumbentTodo(topics));
        return of(undefined);
      }),
      catchError(() => {
        this.topicsStateModel.update(state => ({ ...state, loaded: false, loading: false }));
        this.userState.setDynamicMercerAccess(false);
        return of(undefined);
      })
    );
  }

  private loadTopicsSuccess(payload: Topic[]): void {
    const { bannerTopics } = this.topicsStateModel();
    const newBannerTopics = this.mapBannerTopics(
      bannerTopics,
      payload.filter(topic => topic.type === CommunicationTypeEnum.Banner)
    );
    const topics = payload.filter(topic => topic.type !== CommunicationTypeEnum.Banner);
    this.topicsStateModel.update(state => ({ ...state, topics, bannerTopics: newBannerTopics }));

    this.extractCategories();
    this.topicsStateModel.update(state => ({ ...state, loaded: true, loading: false }));
  }

  private hasFinalIncumbentTodo(topics: Topic[]): boolean {
    return topics.some(
      topic => topic.type === CommunicationTypeEnum.Task && topic.taskType === TodoTaskType.FinalIncumbent
    );
  }

  updateBannerVisibility(payload: Topic): void {
    this.topicsStateModel.update(state =>
      produce(state, draft => {
        const foundIndex = draft.bannerTopics.findIndex(topic => topic.id === payload.id);
        if (foundIndex >= 0) {
          draft.bannerTopics[foundIndex].showBanner = false;
        }
      })
    );
  }

  updateCategories(payload: string): void {
    const currentState = this.topicsStateModel();

    let updatedCategories: Category[] = [...currentState.topicsFilter.categories];

    if (currentState.topicsFilter.allItemsFilter) {
      updatedCategories = updatedCategories.map(category => ({ type: category.type, active: false }));
    }

    updatedCategories = updatedCategories.map(category =>
      category.type === payload ? { type: category.type, active: !category.active } : category
    );

    this.topicsStateModel.update(state => ({
      ...state,
      topicsFilter: { categories: updatedCategories, allItemsFilter: false }
    }));
  }

  resetCategories(): void {
    const currentState = this.topicsStateModel();
    const updatedCategories: Category[] = currentState.topicsFilter.categories.map(category => ({
      type: category.type,
      active: true
    }));
    this.topicsStateModel.update(state => ({
      ...state,
      topicsFilter: { categories: updatedCategories, allItemsFilter: true }
    }));
  }

  private extractCategories(): void {
    const currentState = this.topicsStateModel();
    const types: string[] = [];
    const categories: Category[] = [];

    for (const topic of currentState.topics) {
      types.push(topic.type);
    }

    for (const itemType of [...new Set(types)]) {
      categories.push({
        type: itemType,
        active: true
      });
    }

    const topicsFilterItem = { ...currentState.topicsFilter };
    topicsFilterItem.categories = categories;
    this.topicsStateModel.update(state => ({ ...state, topicsFilter: topicsFilterItem }));
  }

  private mapBannerTopics(currentTopics: Topic[], newTopics: Topic[]): Topic[] {
    return newTopics.map(topic => {
      const matchingTopic = currentTopics?.find(currentTopic => currentTopic.id === topic.id);
      if (matchingTopic) {
        topic.showBanner = matchingTopic.showBanner;
      }
      return topic;
    });
  }
}
